import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  FiEdit3, FiFileText, FiBookOpen, FiClock, FiCalendar,
  FiTrendingUp, FiActivity, FiAlertCircle,
  FiBarChart2, FiPlusCircle, FiUsers, FiPieChart,
} from 'react-icons/fi';
import {
  BarChart, Bar, LineChart, Line, AreaChart, Area, PieChart, Pie, Cell,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { apiGetAllResearch } from '../../api/researchApi';
import { apiGetAllPosts } from '../../api/blogApi';
import { useAuth } from '../../context/AuthContext';
import GoogleSignInGate from '../../components/GoogleSignInGate';
import UserBadge from '../../components/UserBadge';
import NewsletterWeek from './NewsletterWeek';

const ACCENT = '#6cb4ff';
const GREEN = '#34d399';
const PURPLE = '#a78bfa';
const AMBER = '#fbbf24';
const COLORS = [ACCENT, PURPLE, GREEN, AMBER, '#f87171', '#22d3ee', '#818cf8', '#fb923c'];

/* ─── helpers ─── */
function wordCount(text) {
  if (!text || typeof text !== 'string') return 0;
  return text.split(/\s+/).filter(Boolean).length;
}

function daysAgo(dateStr) {
  if (!dateStr) return Infinity;
  return Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
}

function streakDays(items) {
  const dates = items
    .map((i) => i.publishedDate)
    .filter(Boolean)
    .map((d) => new Date(d).toISOString().slice(0, 10))
    .sort()
    .reverse();
  const unique = [...new Set(dates)];
  let streak = 0;
  const now = new Date();
  for (let i = 0; i < unique.length; i++) {
    const expected = new Date(now);
    expected.setDate(expected.getDate() - i);
    if (unique[i] === expected.toISOString().slice(0, 10)) streak++;
    else break;
  }
  return streak;
}

/* ── Custom Tooltip ── */
const Tip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="ad-tooltip">
      <p className="ad-tooltip-label">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color || p.fill }}>{p.name}: <strong>{p.value}</strong></p>
      ))}
    </div>
  );
};

export default function WritingDashboard() {
  useAuth();
  const [research, setResearch] = useState([]);
  const [posts, setPosts] = useState([]);
  const [activeView, setActiveView] = useState('pipeline');

  useEffect(() => {
    apiGetAllResearch().then((r) => { if (r.ok) setResearch(r.data); });
    apiGetAllPosts().then((r) => { if (r.ok) setPosts(r.data); });
  }, []);

  const allContent = useMemo(() => [...research, ...posts], [research, posts]);

  /* ── KPIs ── */
  const totalPieces = allContent.length;
  const drafts = allContent.filter((c) => c.status === 'draft');
  const published = allContent.filter((c) => c.status === 'published');
  const totalWords = allContent.reduce((s, c) => {
    let w = wordCount(c.abstract || c.excerpt || '');
    (c.sections || []).forEach((sec) => { w += wordCount(sec.content || ''); });
    return s + w;
  }, 0);
  const streak = streakDays(published);

  /* ── Word count breakdown ── */
  const wordsByType = useMemo(() => [
    {
      name: 'Research',
      words: research.reduce((s, r) => {
        let w = wordCount(r.abstract || '');
        (r.sections || []).forEach((sec) => { w += wordCount(sec.content || ''); });
        return s + w;
      }, 0),
    },
    {
      name: 'Blog',
      words: posts.reduce((s, p) => {
        let w = wordCount(p.excerpt || '');
        (p.sections || []).forEach((sec) => { w += wordCount(sec.content || ''); });
        return s + w;
      }, 0),
    },
  ], [research, posts]);

  /* ── Publishing cadence (last 12 months) ── */
  const cadence = useMemo(() => {
    const months = {};
    const now = new Date();
    for (let i = 11; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = d.toISOString().slice(0, 7);
      const label = d.toLocaleDateString('en-US', { month: 'short' });
      months[key] = { name: label, research: 0, blog: 0 };
    }
    research.filter((r) => r.publishedDate).forEach((r) => {
      const key = new Date(r.publishedDate).toISOString().slice(0, 7);
      if (months[key]) months[key].research++;
    });
    posts.filter((p) => p.publishedDate).forEach((p) => {
      const key = new Date(p.publishedDate).toISOString().slice(0, 7);
      if (months[key]) months[key].blog++;
    });
    return Object.values(months);
  }, [research, posts]);

  /* ── Drafts queue (oldest first) ── */
  const draftQueue = useMemo(() => {
    return drafts
      .map((d) => ({
        ...d,
        kind: d.authors ? 'research' : 'blog',
        age: daysAgo(d.createdAt || d.updatedAt),
        wc: (() => {
          let w = wordCount(d.abstract || d.excerpt || '');
          (d.sections || []).forEach((sec) => { w += wordCount(sec.content || ''); });
          return w;
        })(),
      }))
      .sort((a, b) => b.age - a.age);
  }, [drafts]);

  /* ── Category spread ── */
  const categorySpread = useMemo(() => {
    const map = {};
    allContent.forEach((c) => {
      const cat = c.category || 'Other';
      map[cat] = (map[cat] || 0) + 1;
    });
    return Object.entries(map)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 8)
      .map(([name, value]) => ({ name, value }));
  }, [allContent]);

  /* ── Recent activity ── */
  const recentActivity = useMemo(() => {
    return [...allContent]
      .filter((c) => c.publishedDate || c.updatedAt)
      .sort((a, b) => new Date(b.publishedDate || b.updatedAt) - new Date(a.publishedDate || a.updatedAt))
      .slice(0, 8)
      .map((c) => ({
        title: c.title,
        type: c.authors ? 'research' : 'blog',
        status: c.status,
        date: c.publishedDate || c.updatedAt || '',
        id: c.id,
        slug: c.slug,
      }));
  }, [allContent]);

  /* ═══ Analytics: Research metrics ═══ */
  const researchByCategory = useMemo(() => {
    const map = {};
    research.forEach((r) => {
      const cat = r.category || 'Uncategorized';
      if (!map[cat]) map[cat] = { name: cat, count: 0, citations: 0 };
      map[cat].count++;
      map[cat].citations += r.citedBy || 0;
    });
    return Object.values(map).sort((a, b) => b.citations - a.citations);
  }, [research]);

  const citationsByPaper = useMemo(() => {
    return [...research]
      .filter((r) => r.status === 'published')
      .sort((a, b) => (b.citedBy || 0) - (a.citedBy || 0))
      .slice(0, 8)
      .map((r) => ({
        name: r.title.length > 35 ? r.title.slice(0, 35) + '\u2026' : r.title,
        citations: r.citedBy || 0,
        fullTitle: r.title,
      }));
  }, [research]);

  const researchTimeline = useMemo(() => {
    const months = {};
    research.filter((r) => r.publishedDate).forEach((r) => {
      const d = new Date(r.publishedDate);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
      if (!months[key]) months[key] = { key, name: label, papers: 0, citations: 0 };
      months[key].papers++;
      months[key].citations += r.citedBy || 0;
    });
    return Object.values(months).sort((a, b) => a.key.localeCompare(b.key));
  }, [research]);

  const researchStatusData = useMemo(() => {
    const pub = research.filter((r) => r.status === 'published').length;
    const draft = research.filter((r) => r.status === 'draft').length;
    return [
      { name: 'Published', value: pub, color: '#059669' },
      { name: 'Draft', value: draft, color: '#f59e0b' },
    ].filter((d) => d.value > 0);
  }, [research]);

  /* ═══ Analytics: Blog metrics ═══ */
  const blogByCategory = useMemo(() => {
    const map = {};
    posts.forEach((p) => {
      const cat = p.category || 'Uncategorized';
      if (!map[cat]) map[cat] = { name: cat, articles: 0, published: 0, drafts: 0 };
      map[cat].articles++;
      if (p.status === 'published') map[cat].published++;
      else map[cat].drafts++;
    });
    return Object.values(map).sort((a, b) => b.articles - a.articles);
  }, [posts]);

  const blogTimeline = useMemo(() => {
    const months = {};
    posts.filter((p) => p.publishedDate).forEach((p) => {
      const d = new Date(p.publishedDate);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
      if (!months[key]) months[key] = { key, name: label, articles: 0, readMinutes: 0 };
      months[key].articles++;
      months[key].readMinutes += p.readTime || 5;
    });
    return Object.values(months).sort((a, b) => a.key.localeCompare(b.key));
  }, [posts]);

  const blogStatusData = useMemo(() => {
    const pub = posts.filter((p) => p.status === 'published').length;
    const draft = posts.filter((p) => p.status === 'draft').length;
    const feat = posts.filter((p) => p.featured).length;
    return [
      { name: 'Published', value: pub, color: '#059669' },
      { name: 'Draft', value: draft, color: '#f59e0b' },
      { name: 'Featured', value: feat, color: '#7c3aed' },
    ].filter((d) => d.value > 0);
  }, [posts]);

  const tagFrequency = useMemo(() => {
    const map = {};
    [...research, ...posts].forEach((item) => {
      (item.tags || []).forEach((t) => {
        const tag = typeof t === 'string' ? t.toLowerCase() : t;
        map[tag] = (map[tag] || 0) + 1;
      });
    });
    return Object.entries(map)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 12)
      .map(([tag, count]) => ({ name: tag, count }));
  }, [research, posts]);

  const authorOutput = useMemo(() => {
    const map = {};
    research.forEach((r) => {
      (r.authors || []).forEach((a) => {
        if (!map[a]) map[a] = { name: a, papers: 0, citations: 0, blogs: 0 };
        map[a].papers++;
        map[a].citations += r.citedBy || 0;
      });
    });
    posts.forEach((p) => {
      const a = p.author || 'Unknown';
      if (!map[a]) map[a] = { name: a, papers: 0, citations: 0, blogs: 0 };
      map[a].blogs++;
    });
    return Object.values(map).sort((a, b) => (b.papers + b.blogs) - (a.papers + a.blogs));
  }, [research, posts]);

  const radarData = useMemo(() => {
    const cats = [...new Set(research.map((r) => r.category))];
    return cats.map((cat) => {
      const items = research.filter((r) => r.category === cat);
      return {
        category: cat.replace(/\s+/g, '\n').slice(0, 20),
        papers: items.length,
        citations: Math.round(items.reduce((s, r) => s + (r.citedBy || 0), 0) / Math.max(items.length, 1)),
      };
    });
  }, [research]);

  /* ═══ Analytics: Overview summaries ═══ */
  const totalCitations = research.reduce((s, r) => s + (r.citedBy || 0), 0);
  const totalReadTime = posts.reduce((s, p) => s + (p.readTime || 0), 0);
  const avgCitations = research.length ? Math.round(totalCitations / research.length) : 0;

  return (
    <GoogleSignInGate>
      <div className="admin-page">
        {/* Header */}
        <div className="admin-header">
          <div className="admin-header-inner">
            <Link to="/" className="research-brand">
              <span className="research-brand-text"><strong>Vector Labs</strong> Admin</span>
            </Link>
            <nav className="admin-nav">
              <Link to="/admin/writing" className="admin-nav-link active">
                <FiEdit3 style={{ marginRight: 4 }} />Writing
              </Link>
              <Link to="/admin/usage" className="admin-nav-link">
                <FiActivity style={{ marginRight: 4 }} />Usage
              </Link>
              <Link to="/" className="admin-nav-link">Home</Link>
            </nav>
            <UserBadge />
          </div>
        </div>

        <div className="admin-body">
          {/* Page header */}
          <div className="ad-page-header">
            <div className="ad-page-title">
              <FiEdit3 className="ad-title-icon" style={{ color: ACCENT }} />
              <div>
                <h1>Writing Dashboard</h1>
                <p>Content pipeline, cadence, and productivity metrics.</p>
              </div>
            </div>
            <div className="ad-view-toggles">
              {[
                { key: 'pipeline', label: 'Pipeline', icon: <FiEdit3 /> },
                { key: 'overview', label: 'Overview', icon: <FiPieChart /> },
                { key: 'research', label: 'Research', icon: <FiBookOpen /> },
                { key: 'blog', label: 'Blog', icon: <FiFileText /> },
              ].map((v) => (
                <button key={v.key}
                  className={`ad-view-btn ${activeView === v.key ? 'active' : ''}`}
                  onClick={() => setActiveView(v.key)}>
                  {v.icon} {v.label}
                </button>
              ))}
            </div>
          </div>

          {activeView === 'pipeline' && (
          <>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            <Link to="/admin/new" className="admin-btn primary"><FiPlusCircle /> New Paper</Link>
            <Link to="/admin/blog/new" className="admin-btn primary" style={{ background: PURPLE }}><FiPlusCircle /> New Article</Link>
          </div>
          <NewsletterWeek />

          {/* KPI cards */}
          <div className="ad-kpi-grid">
            <div className="ad-kpi-card">
              <div className="ad-kpi-icon" style={{ background: 'rgba(108,180,255,0.12)', color: ACCENT }}><FiFileText /></div>
              <div className="ad-kpi-data">
                <span className="ad-kpi-number">{totalPieces}</span>
                <span className="ad-kpi-label">Total Pieces</span>
              </div>
            </div>
            <div className="ad-kpi-card">
              <div className="ad-kpi-icon" style={{ background: 'rgba(251,191,36,0.12)', color: AMBER }}><FiAlertCircle /></div>
              <div className="ad-kpi-data">
                <span className="ad-kpi-number">{drafts.length}</span>
                <span className="ad-kpi-label">Drafts in Pipeline</span>
              </div>
            </div>
            <div className="ad-kpi-card">
              <div className="ad-kpi-icon" style={{ background: 'rgba(52,211,153,0.12)', color: GREEN }}><FiTrendingUp /></div>
              <div className="ad-kpi-data">
                <span className="ad-kpi-number">{totalWords.toLocaleString()}</span>
                <span className="ad-kpi-label">Total Words</span>
              </div>
            </div>
            <div className="ad-kpi-card">
              <div className="ad-kpi-icon" style={{ background: 'rgba(167,139,250,0.12)', color: PURPLE }}><FiCalendar /></div>
              <div className="ad-kpi-data">
                <span className="ad-kpi-number">{streak}</span>
                <span className="ad-kpi-label">Day Streak</span>
              </div>
            </div>
          </div>

          {/* Publishing cadence */}
          <div className="ad-chart-row ad-chart-row--full">
            <div className="ad-chart-card">
              <h3><FiBarChart2 /> Publishing Cadence (Last 12 Months)</h3>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={cadence} margin={{ left: 0, right: 10, top: 5, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="name" stroke="#94a3b8" fontSize={12} tick={{ fill: 'rgba(255,255,255,0.45)' }} />
                  <YAxis stroke="#94a3b8" fontSize={12} allowDecimals={false} />
                  <Tooltip content={<Tip />} />
                  <Legend />
                  <Bar dataKey="research" name="Research" fill={ACCENT} radius={[4, 4, 0, 0]} stackId="a" animationDuration={1000} />
                  <Bar dataKey="blog" name="Blog" fill={PURPLE} radius={[4, 4, 0, 0]} stackId="a" animationDuration={1200} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Word count + Category spread */}
          <div className="ad-chart-row ad-chart-row--1-1">
            <div className="ad-chart-card">
              <h3><FiBarChart2 /> Word Count by Type</h3>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={wordsByType} margin={{ left: 0, right: 10, top: 5, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="name" stroke="#94a3b8" fontSize={12} tick={{ fill: 'rgba(255,255,255,0.45)' }} />
                  <YAxis stroke="#94a3b8" fontSize={12} />
                  <Tooltip content={<Tip />} />
                  <Bar dataKey="words" name="Words" radius={[6, 6, 0, 0]} animationDuration={1000}>
                    {wordsByType.map((_, i) => <Cell key={i} fill={COLORS[i]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="ad-chart-card">
              <h3><FiBookOpen /> Category Spread</h3>
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie data={categorySpread} cx="50%" cy="50%" innerRadius={50} outerRadius={90}
                    paddingAngle={3} dataKey="value" animationDuration={1200}
                    label={({ name, value }) => `${name.slice(0, 16)} (${value})`}>
                    {categorySpread.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip content={<Tip />} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Drafts queue + Recent activity */}
          <div className="wd-bottom-row">
            {/* Drafts Pipeline */}
            <div className="ad-chart-card wd-drafts-card">
              <h3><FiAlertCircle /> Drafts Pipeline</h3>
              {draftQueue.length === 0 ? (
                <p style={{ color: 'rgba(255,255,255,0.35)', textAlign: 'center', padding: '2rem 0' }}>
                  No drafts — everything's published!
                </p>
              ) : (
                <div className="wd-draft-list">
                  {draftQueue.map((d) => (
                    <Link
                      key={d.id}
                      to={d.kind === 'research' ? `/admin/edit/${d.id}` : `/admin/blog/edit/${d.id}`}
                      className="wd-draft-item"
                    >
                      <span className={`wd-draft-kind ${d.kind}`}>
                        {d.kind === 'research' ? <FiBookOpen /> : <FiFileText />}
                      </span>
                      <div className="wd-draft-info">
                        <span className="wd-draft-title">{d.title || 'Untitled'}</span>
                        <span className="wd-draft-meta">
                          {d.wc.toLocaleString()} words · {d.age < Infinity ? `${d.age}d old` : 'new'}
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Recent Activity */}
            <div className="ad-chart-card wd-activity-card">
              <h3><FiClock /> Recent Activity</h3>
              {recentActivity.length === 0 ? (
                <p style={{ color: 'rgba(255,255,255,0.35)', textAlign: 'center', padding: '2rem 0' }}>
                  No activity yet.
                </p>
              ) : (
                <div className="wd-activity-list">
                  {recentActivity.map((item, i) => (
                    <Link
                      key={i}
                      to={item.type === 'research' ? `/research/${item.id}` : `/blog/${item.slug || item.id}`}
                      className="wd-activity-item"
                    >
                      <span className={`wd-activity-dot ${item.status}`} />
                      <div className="wd-activity-info">
                        <span className="wd-activity-title">{item.title}</span>
                        <span className="wd-activity-meta">
                          {item.type === 'research' ? 'Paper' : 'Article'} · {item.status} · {item.date ? new Date(item.date).toLocaleDateString() : '—'}
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
          </>
          )}

          {/* ════════════════════════════════════════════════
             OVERVIEW
             ════════════════════════════════════════════════ */}
          {activeView === 'overview' && (
            <>
              <div className="ad-kpi-grid">
                <div className="ad-kpi-card">
                  <div className="ad-kpi-icon" style={{ background: 'rgba(0,82,204,0.12)', color: '#0052cc' }}><FiBookOpen /></div>
                  <div className="ad-kpi-data">
                    <span className="ad-kpi-number">{research.length}</span>
                    <span className="ad-kpi-label">Publications</span>
                  </div>
                </div>
                <div className="ad-kpi-card">
                  <div className="ad-kpi-icon" style={{ background: 'rgba(124,58,237,0.12)', color: '#7c3aed' }}><FiFileText /></div>
                  <div className="ad-kpi-data">
                    <span className="ad-kpi-number">{posts.length}</span>
                    <span className="ad-kpi-label">Blog Articles</span>
                  </div>
                </div>
                <div className="ad-kpi-card">
                  <div className="ad-kpi-icon" style={{ background: 'rgba(5,150,105,0.12)', color: '#059669' }}><FiTrendingUp /></div>
                  <div className="ad-kpi-data">
                    <span className="ad-kpi-number">{totalCitations.toLocaleString()}</span>
                    <span className="ad-kpi-label">Total Citations</span>
                  </div>
                </div>
                <div className="ad-kpi-card">
                  <div className="ad-kpi-icon" style={{ background: 'rgba(217,119,6,0.12)', color: '#d97706' }}><FiUsers /></div>
                  <div className="ad-kpi-data">
                    <span className="ad-kpi-number">{authorOutput.length}</span>
                    <span className="ad-kpi-label">Authors</span>
                  </div>
                </div>
              </div>

              <div className="ad-chart-row ad-chart-row--2-1">
                <div className="ad-chart-card">
                  <h3><FiBarChart2 /> Citations by Paper</h3>
                  <ResponsiveContainer width="100%" height={320}>
                    <BarChart data={citationsByPaper} layout="vertical" margin={{ left: 10, right: 20, top: 5, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                      <XAxis type="number" stroke="#94a3b8" fontSize={12} />
                      <YAxis type="category" dataKey="name" width={150} stroke="#94a3b8" fontSize={11} tick={{ fill: 'rgba(255,255,255,0.45)' }} />
                      <Tooltip content={<Tip />} />
                      <Bar dataKey="citations" radius={[0, 6, 6, 0]} animationDuration={1200} animationEasing="ease-out">
                        {citationsByPaper.map((_, i) => (
                          <Cell key={i} fill={COLORS[i % COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                <div className="ad-chart-card">
                  <h3><FiPieChart /> Content Mix</h3>
                  <div className="ad-pie-row">
                    <div className="ad-pie-wrap">
                      <p className="ad-pie-title">Research</p>
                      <ResponsiveContainer width="100%" height={130}>
                        <PieChart>
                          <Pie data={researchStatusData} cx="50%" cy="50%" innerRadius={30} outerRadius={55}
                            paddingAngle={4} dataKey="value" animationDuration={1000} animationEasing="ease-out">
                            {researchStatusData.map((d, i) => <Cell key={i} fill={d.color} />)}
                          </Pie>
                          <Tooltip content={<Tip />} />
                        </PieChart>
                      </ResponsiveContainer>
                      <div className="ad-pie-legend">
                        {researchStatusData.map((d) => (
                          <span key={d.name}><span className="ad-legend-dot" style={{ background: d.color }} />{d.name} ({d.value})</span>
                        ))}
                      </div>
                    </div>
                    <div className="ad-pie-wrap">
                      <p className="ad-pie-title">Blog</p>
                      <ResponsiveContainer width="100%" height={130}>
                        <PieChart>
                          <Pie data={blogStatusData} cx="50%" cy="50%" innerRadius={30} outerRadius={55}
                            paddingAngle={4} dataKey="value" animationDuration={1000} animationEasing="ease-out">
                            {blogStatusData.map((d, i) => <Cell key={i} fill={d.color} />)}
                          </Pie>
                          <Tooltip content={<Tip />} />
                        </PieChart>
                      </ResponsiveContainer>
                      <div className="ad-pie-legend">
                        {blogStatusData.map((d) => (
                          <span key={d.name}><span className="ad-legend-dot" style={{ background: d.color }} />{d.name} ({d.value})</span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="ad-chart-row ad-chart-row--1-1">
                <div className="ad-chart-card">
                  <h3><FiBarChart2 /> Top Tags Across All Content</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={tagFrequency} margin={{ left: 0, right: 10, top: 5, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                      <XAxis dataKey="name" stroke="#94a3b8" fontSize={11} angle={-30} textAnchor="end" height={60} tick={{ fill: 'rgba(255,255,255,0.45)' }} />
                      <YAxis stroke="#94a3b8" fontSize={12} allowDecimals={false} />
                      <Tooltip content={<Tip />} />
                      <Bar dataKey="count" name="Uses" fill="#7c3aed" radius={[6, 6, 0, 0]} animationDuration={1000} animationEasing="ease-out" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                <div className="ad-chart-card">
                  <h3><FiUsers /> Author Contributions</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={authorOutput} margin={{ left: 0, right: 10, top: 5, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                      <XAxis dataKey="name" stroke="#94a3b8" fontSize={11} angle={-20} textAnchor="end" height={60} tick={{ fill: 'rgba(255,255,255,0.45)' }} />
                      <YAxis stroke="#94a3b8" fontSize={12} allowDecimals={false} />
                      <Tooltip content={<Tip />} />
                      <Legend />
                      <Bar dataKey="papers" name="Papers" fill="#0052cc" radius={[4, 4, 0, 0]} stackId="a" animationDuration={1000} />
                      <Bar dataKey="blogs" name="Blog Posts" fill="#7c3aed" radius={[4, 4, 0, 0]} stackId="a" animationDuration={1200} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </>
          )}

          {/* ════════════════════════════════════════════════
             RESEARCH VIEW
             ════════════════════════════════════════════════ */}
          {activeView === 'research' && (
            <>
              <div className="ad-kpi-grid">
                <div className="ad-kpi-card">
                  <div className="ad-kpi-icon" style={{ background: 'rgba(0,82,204,0.12)', color: '#0052cc' }}><FiBookOpen /></div>
                  <div className="ad-kpi-data">
                    <span className="ad-kpi-number">{research.filter((r) => r.status === 'published').length}</span>
                    <span className="ad-kpi-label">Published</span>
                  </div>
                </div>
                <div className="ad-kpi-card">
                  <div className="ad-kpi-icon" style={{ background: 'rgba(5,150,105,0.12)', color: '#059669' }}><FiTrendingUp /></div>
                  <div className="ad-kpi-data">
                    <span className="ad-kpi-number">{totalCitations.toLocaleString()}</span>
                    <span className="ad-kpi-label">Total Citations</span>
                  </div>
                </div>
                <div className="ad-kpi-card">
                  <div className="ad-kpi-icon" style={{ background: 'rgba(217,119,6,0.12)', color: '#d97706' }}><FiBarChart2 /></div>
                  <div className="ad-kpi-data">
                    <span className="ad-kpi-number">{avgCitations}</span>
                    <span className="ad-kpi-label">Avg Citations/Paper</span>
                  </div>
                </div>
                <div className="ad-kpi-card">
                  <div className="ad-kpi-icon" style={{ background: 'rgba(124,58,237,0.12)', color: '#7c3aed' }}><FiCalendar /></div>
                  <div className="ad-kpi-data">
                    <span className="ad-kpi-number">{researchTimeline.length}</span>
                    <span className="ad-kpi-label">Active Months</span>
                  </div>
                </div>
              </div>

              <div className="ad-chart-row ad-chart-row--full">
                <div className="ad-chart-card">
                  <h3><FiTrendingUp /> Publication Timeline</h3>
                  <ResponsiveContainer width="100%" height={320}>
                    <AreaChart data={researchTimeline} margin={{ left: 0, right: 10, top: 10, bottom: 5 }}>
                      <defs>
                        <linearGradient id="gradPapers" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#0052cc" stopOpacity={0.3} />
                          <stop offset="100%" stopColor="#0052cc" stopOpacity={0.02} />
                        </linearGradient>
                        <linearGradient id="gradCite" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#7c3aed" stopOpacity={0.3} />
                          <stop offset="100%" stopColor="#7c3aed" stopOpacity={0.02} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                      <XAxis dataKey="name" stroke="#94a3b8" fontSize={12} tick={{ fill: 'rgba(255,255,255,0.45)' }} />
                      <YAxis yAxisId="left" stroke="#0052cc" fontSize={12} allowDecimals={false} />
                      <YAxis yAxisId="right" orientation="right" stroke="#7c3aed" fontSize={12} />
                      <Tooltip content={<Tip />} />
                      <Legend />
                      <Area yAxisId="left" type="monotone" dataKey="papers" name="Papers" stroke="#0052cc" fill="url(#gradPapers)"
                        strokeWidth={2} dot={{ r: 4, fill: '#0052cc' }} activeDot={{ r: 6 }} animationDuration={1500} />
                      <Area yAxisId="right" type="monotone" dataKey="citations" name="Citations" stroke="#7c3aed" fill="url(#gradCite)"
                        strokeWidth={2} dot={{ r: 4, fill: '#7c3aed' }} activeDot={{ r: 6 }} animationDuration={1800} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="ad-chart-row ad-chart-row--1-1">
                <div className="ad-chart-card">
                  <h3><FiBarChart2 /> Papers & Citations by Category</h3>
                  <ResponsiveContainer width="100%" height={320}>
                    <BarChart data={researchByCategory} margin={{ left: 0, right: 10, top: 5, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                      <XAxis dataKey="name" stroke="#94a3b8" fontSize={10} angle={-25} textAnchor="end" height={70} tick={{ fill: 'rgba(255,255,255,0.45)' }} />
                      <YAxis yAxisId="left" stroke="#0052cc" fontSize={12} allowDecimals={false} />
                      <YAxis yAxisId="right" orientation="right" stroke="#059669" fontSize={12} />
                      <Tooltip content={<Tip />} />
                      <Legend />
                      <Bar yAxisId="left" dataKey="count" name="Papers" fill="#0052cc" radius={[6, 6, 0, 0]} animationDuration={1000} />
                      <Bar yAxisId="right" dataKey="citations" name="Citations" fill="#059669" radius={[6, 6, 0, 0]} animationDuration={1200} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                <div className="ad-chart-card">
                  <h3><FiActivity /> Research Focus Radar</h3>
                  <ResponsiveContainer width="100%" height={320}>
                    <RadarChart data={radarData} outerRadius="70%">
                      <PolarGrid stroke="rgba(255,255,255,0.06)" />
                      <PolarAngleAxis dataKey="category" fontSize={10} tick={{ fill: 'rgba(255,255,255,0.45)' }} />
                      <PolarRadiusAxis fontSize={10} stroke="rgba(255,255,255,0.15)" />
                      <Tooltip content={<Tip />} />
                      <Radar name="Papers" dataKey="papers" stroke="#0052cc" fill="#0052cc" fillOpacity={0.25} animationDuration={1500} />
                      <Radar name="Avg Citations" dataKey="citations" stroke="#7c3aed" fill="#7c3aed" fillOpacity={0.2} animationDuration={1800} />
                      <Legend />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="ad-chart-row ad-chart-row--full">
                <div className="ad-chart-card">
                  <h3><FiTrendingUp /> Citation Leaderboard</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={citationsByPaper} margin={{ left: 0, right: 10, top: 5, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                      <XAxis dataKey="name" stroke="#94a3b8" fontSize={10} angle={-15} textAnchor="end" height={60} tick={{ fill: 'rgba(255,255,255,0.45)' }} />
                      <YAxis stroke="#94a3b8" fontSize={12} />
                      <Tooltip content={<Tip />} />
                      <Bar dataKey="citations" name="Citations" radius={[6, 6, 0, 0]} animationDuration={1200}>
                        {citationsByPaper.map((_, i) => (
                          <Cell key={i} fill={COLORS[i % COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </>
          )}

          {/* ════════════════════════════════════════════════
             BLOG VIEW
             ════════════════════════════════════════════════ */}
          {activeView === 'blog' && (
            <>
              <div className="ad-kpi-grid">
                <div className="ad-kpi-card">
                  <div className="ad-kpi-icon" style={{ background: 'rgba(124,58,237,0.12)', color: '#7c3aed' }}><FiFileText /></div>
                  <div className="ad-kpi-data">
                    <span className="ad-kpi-number">{posts.filter((p) => p.status === 'published').length}</span>
                    <span className="ad-kpi-label">Published</span>
                  </div>
                </div>
                <div className="ad-kpi-card">
                  <div className="ad-kpi-icon" style={{ background: 'rgba(217,119,6,0.12)', color: '#d97706' }}><FiCalendar /></div>
                  <div className="ad-kpi-data">
                    <span className="ad-kpi-number">{posts.filter((p) => p.status === 'draft').length}</span>
                    <span className="ad-kpi-label">Drafts</span>
                  </div>
                </div>
                <div className="ad-kpi-card">
                  <div className="ad-kpi-icon" style={{ background: 'rgba(5,150,105,0.12)', color: '#059669' }}><FiTrendingUp /></div>
                  <div className="ad-kpi-data">
                    <span className="ad-kpi-number">{totalReadTime}</span>
                    <span className="ad-kpi-label">Total Read Min</span>
                  </div>
                </div>
                <div className="ad-kpi-card">
                  <div className="ad-kpi-icon" style={{ background: 'rgba(0,82,204,0.12)', color: '#0052cc' }}><FiBarChart2 /></div>
                  <div className="ad-kpi-data">
                    <span className="ad-kpi-number">{posts.filter((p) => p.featured).length}</span>
                    <span className="ad-kpi-label">Featured</span>
                  </div>
                </div>
              </div>

              <div className="ad-chart-row ad-chart-row--full">
                <div className="ad-chart-card">
                  <h3><FiTrendingUp /> Blog Publishing Timeline</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={blogTimeline} margin={{ left: 0, right: 10, top: 10, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                      <XAxis dataKey="name" stroke="#94a3b8" fontSize={12} tick={{ fill: 'rgba(255,255,255,0.45)' }} />
                      <YAxis yAxisId="left" stroke="#7c3aed" fontSize={12} allowDecimals={false} />
                      <YAxis yAxisId="right" orientation="right" stroke="#059669" fontSize={12} />
                      <Tooltip content={<Tip />} />
                      <Legend />
                      <Line yAxisId="left" type="monotone" dataKey="articles" name="Articles" stroke="#7c3aed"
                        strokeWidth={3} dot={{ r: 5, fill: '#7c3aed' }} activeDot={{ r: 7, stroke: '#fff', strokeWidth: 2 }}
                        animationDuration={1500} />
                      <Line yAxisId="right" type="monotone" dataKey="readMinutes" name="Read Minutes" stroke="#059669"
                        strokeWidth={2} strokeDasharray="6 3" dot={{ r: 4, fill: '#059669' }}
                        animationDuration={1800} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="ad-chart-row ad-chart-row--1-1">
                <div className="ad-chart-card">
                  <h3><FiBarChart2 /> Articles by Category</h3>
                  <ResponsiveContainer width="100%" height={320}>
                    <BarChart data={blogByCategory} margin={{ left: 0, right: 10, top: 5, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                      <XAxis dataKey="name" stroke="#94a3b8" fontSize={10} angle={-20} textAnchor="end" height={60} tick={{ fill: 'rgba(255,255,255,0.45)' }} />
                      <YAxis stroke="#94a3b8" fontSize={12} allowDecimals={false} />
                      <Tooltip content={<Tip />} />
                      <Legend />
                      <Bar dataKey="published" name="Published" fill="#059669" stackId="a" radius={[0, 0, 0, 0]} animationDuration={1000} />
                      <Bar dataKey="drafts" name="Drafts" fill="#f59e0b" stackId="a" radius={[6, 6, 0, 0]} animationDuration={1200} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                <div className="ad-chart-card">
                  <h3><FiPieChart /> Blog Status Breakdown</h3>
                  <ResponsiveContainer width="100%" height={320}>
                    <PieChart>
                      <Pie data={blogStatusData} cx="50%" cy="50%" innerRadius={60} outerRadius={100}
                        paddingAngle={4} dataKey="value" label={({ name, value }) => `${name} (${value})`}
                        animationDuration={1200} animationEasing="ease-out">
                        {blogStatusData.map((d, i) => <Cell key={i} fill={d.color} />)}
                      </Pie>
                      <Tooltip content={<Tip />} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="ad-chart-row ad-chart-row--full">
                <div className="ad-chart-card">
                  <h3><FiBarChart2 /> Read Time Distribution</h3>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart
                      data={(() => {
                        const bins = [
                          { range: '1-5 min', min: 1, max: 5, count: 0 },
                          { range: '6-10 min', min: 6, max: 10, count: 0 },
                          { range: '11-15 min', min: 11, max: 15, count: 0 },
                          { range: '16-20 min', min: 16, max: 20, count: 0 },
                          { range: '20+ min', min: 21, max: 999, count: 0 },
                        ];
                        posts.forEach((p) => {
                          const rt = p.readTime || 5;
                          const bin = bins.find((b) => rt >= b.min && rt <= b.max);
                          if (bin) bin.count++;
                        });
                        return bins.map(({ range, count }) => ({ range, count }));
                      })()}
                      margin={{ left: 0, right: 10, top: 5, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                      <XAxis dataKey="range" stroke="#94a3b8" fontSize={12} tick={{ fill: 'rgba(255,255,255,0.45)' }} />
                      <YAxis stroke="#94a3b8" fontSize={12} allowDecimals={false} />
                      <Tooltip content={<Tip />} />
                      <Bar dataKey="count" name="Articles" fill="#ec4899" radius={[8, 8, 0, 0]} animationDuration={1200}>
                        {[0, 1, 2, 3, 4].map((i) => (
                          <Cell key={i} fill={['#c084fc', '#a78bfa', '#8b5cf6', '#7c3aed', '#6d28d9'][i]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </>
          )}

        </div>
      </div>
    </GoogleSignInGate>
  );
}
